/**
 * API 호출 래퍼. 세션은 httpOnly 쿠키라 토큰을 직접 다루지 않는다.
 * 개발 중에는 Vite가 /api·/auth를 Hono(:3000)로 프록시하므로 same-origin이다.
 */

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      credentials: 'same-origin',
      headers: init?.body ? { 'content-type': 'application/json' } : undefined,
      ...init,
    });
  } catch {
    // 네트워크 자체가 실패한 경우. 사용자에겐 조용하게.
    throw new ApiError('network', '연결이 불안정해요. 잠시 후 다시 시도해 주세요.', 0);
  }

  /**
   * 우리 API는 언제나 JSON을 준다. JSON이 아니면 응답이 서버까지 안 갔다는 뜻이다 —
   * 개발 중 프록시가 엉뚱한 곳(다른 프로젝트)을 가리키거나 서버가 죽은 경우다.
   * 이걸 일반 오류와 뭉뚱그리면 "잠시 후 다시 시도해 주세요"만 뜨고 원인을 못 찾는다.
   */
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    console.error(
      `[api] ${path} 응답이 JSON이 아닙니다 (status=${res.status}, content-type=${contentType || '없음'}).\n` +
        '서버가 떠 있는지, Vite 프록시가 올바른 포트를 가리키는지 확인하세요.',
    );
    throw new ApiError('unreachable', '서버에 연결하지 못했어요.', res.status);
  }

  const body: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    const err = (body as ErrorBody | null)?.error;
    throw new ApiError(
      err?.code ?? 'unknown',
      err?.message ?? '잠시 후 다시 시도해 주세요.',
      res.status,
    );
  }
  return body as T;
}

export const api = {
  get: <T,>(path: string) => request<T>(path),
  post: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  put: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) }),
  patch: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body ?? {}) }),
  del: <T,>(path: string) => request<T>(path, { method: 'DELETE' }),
};
